import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../../api/baseURL';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const priceRange = (p) =>
  (typeof p.priceMin === 'number' && typeof p.priceMax === 'number')
    ? `${fmt(p.priceMin)}–${fmt(p.priceMax)} FCFA` : '—';

const STATUS_BADGE = {
  pending: 'bg-amber-500/20 text-amber-300',
  verified: 'bg-emerald-500/20 text-emerald-300',
  rejected: 'bg-red-500/20 text-red-300',
  merged: 'bg-slate-600/30 text-slate-300',
};

const TABS = [
  { key: 'pending', label: 'En attente' },
  { key: 'conflict', label: 'Conflits' },
  { key: 'verified', label: 'Validées' },
  { key: 'rejected', label: 'Rejetées' },
];

export default function CatalogValidation() {
  const token = localStorage.getItem('authToken');
  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');

  const { data: stats } = useQuery({
    queryKey: ['catalog-stats'],
    queryFn: async () => (await axios.get(`${API_URL}/admin/catalog/stats`, { headers: { Authorization: `Bearer ${token}` } })).data.data,
    refetchInterval: 60000,
  });

  const { data: list, isLoading } = useQuery({
    queryKey: ['catalog-parts', tab, q],
    queryFn: async () => {
      const params = {};
      if (tab === 'conflict') params.conflict = 'true';
      else params.status = tab;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${API_URL}/admin/catalog/parts`, { params, headers: { Authorization: `Bearer ${token}` } });
      return res.data.data;
    },
  });

  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // part being edited
  const [mergeFor, setMergeFor] = useState(null); // part being merged
  const [busyId, setBusyId] = useState(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['catalog-parts'] });
    queryClient.invalidateQueries({ queryKey: ['catalog-stats'] });
  };
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const act = async (id, path) => {
    setBusyId(id);
    try { await axios.post(`${API_URL}/admin/catalog/parts/${id}/${path}`, {}, authHeaders); refresh(); }
    catch (e) { alert(e.response?.data?.error || 'Action échouée'); }
    finally { setBusyId(null); }
  };

  const saveEdit = async (id, body) => {
    try { await axios.patch(`${API_URL}/admin/catalog/parts/${id}`, body, authHeaders); setEditing(null); refresh(); }
    catch (e) { alert(e.response?.data?.error || 'Échec'); }
  };

  const doMerge = async (srcId, targetId) => {
    try { await axios.post(`${API_URL}/admin/catalog/parts/${srcId}/merge`, { targetId }, authHeaders); setMergeFor(null); refresh(); }
    catch (e) { alert(e.response?.data?.error || 'Échec'); }
  };

  const items = list?.items || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Catalogue Pièces</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ['En attente', stats?.pending, 'from-amber-600 to-amber-900'],
          ['Conflits', stats?.conflicts, 'from-red-600 to-red-900'],
          ['Validées', stats?.verified, 'from-emerald-600 to-emerald-900'],
          ['Rejetées', stats?.rejected, 'from-slate-600 to-slate-800'],
          ['Total', stats?.total, 'from-cyan-600 to-cyan-900'],
        ].map(([label, val, grad]) => (
          <div key={label} className={`rounded-xl p-4 bg-gradient-to-br ${grad}`}>
            <p className="text-white/70 text-xs uppercase">{label}</p>
            <p className="text-2xl font-bold text-white">{fmt(val)}</p>
          </div>
        ))}
      </div>

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm ${tab === t.key ? 'bg-cyan-700 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            {t.label}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher référence / marque / nom..."
          className="ml-auto bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white w-72" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-xl">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-slate-400 text-left border-b border-slate-800">
              <th className="p-3">Référence</th>
              <th className="p-3">Marque</th>
              <th className="p-3">Désignation</th>
              <th className="p-3">Véhicules</th>
              <th className="p-3 text-right">Prix</th>
              <th className="p-3 text-right">Usage</th>
              <th className="p-3">Statut</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">Chargement...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">Aucune pièce.</td></tr>
            ) : items.map((p) => (
              <tr key={p._id} className="border-b border-slate-800/60 text-slate-200">
                <td className="p-3 font-mono font-bold">{p.reference ? p.reference : <span className="text-amber-400 font-sans font-normal italic">sans réf.</span>}{p.hasConflict && <span className="ml-1 text-red-400" title={p.conflictNote}>⚠️</span>}</td>
                <td className="p-3">{p.brand || '—'}</td>
                <td className="p-3 text-slate-400">{p.canonicalName || '—'}</td>
                <td className="p-3 text-slate-400">{(p.makes || []).slice(0, 3).join(', ') || '—'}</td>
                <td className="p-3 text-right text-slate-400">{priceRange(p)}</td>
                <td className="p-3 text-right text-slate-400">{fmt(p.usageCount)}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[p.status] || ''}`}>{p.status}</span></td>
                <td className="p-3">
                  <div className="flex gap-1 flex-wrap">
                    {p.status !== 'verified' && (
                      <button disabled={busyId === p._id} onClick={() => act(p._id, 'verify')} className="text-xs px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50">Valider</button>
                    )}
                    <button onClick={() => setEditing(p)} className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white">Corriger</button>
                    <button onClick={() => setMergeFor(p)} className="text-xs px-2 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white">Fusionner</button>
                    {p.status !== 'rejected' && (
                      <button disabled={busyId === p._id} onClick={() => act(p._id, 'reject')} className="text-xs px-2 py-1 rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-50">Rejeter</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <EditModal part={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}

      {/* Merge modal */}
      {mergeFor && (
        <MergeModal part={mergeFor} token={token} onClose={() => setMergeFor(null)} onMerge={doMerge} />
      )}
    </div>
  );
}

function EditModal({ part, onClose, onSave }) {
  const [f, setF] = useState({
    reference: part.reference || '', brand: part.brand || '',
    canonicalName: part.canonicalName || '', category: part.category || '',
    type: part.type || '',
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const cls = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm';
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white">Corriger la pièce</h3>
        <div><label className="text-xs text-slate-400">Référence</label><input className={cls} value={f.reference} onChange={set('reference')} /></div>
        <div><label className="text-xs text-slate-400">Marque</label><input className={cls} value={f.brand} onChange={set('brand')} /></div>
        <div><label className="text-xs text-slate-400">Désignation</label><input className={cls} value={f.canonicalName} onChange={set('canonicalName')} /></div>
        <div><label className="text-xs text-slate-400">Catégorie</label><input className={cls} value={f.category} onChange={set('category')} /></div>
        <div><label className="text-xs text-slate-400">Type</label>
          <select className={cls} value={f.type} onChange={set('type')}>
            <option value="">—</option><option value="OEM">OEM</option><option value="AFTERMARKET">Aftermarket</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm">Annuler</button>
          <button onClick={() => onSave(part._id, { ...f, type: f.type || null })} className="px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function MergeModal({ part, token, onClose, onMerge }) {
  const [q, setQ] = useState(part.canonicalName || part.reference || '');
  const { data } = useQuery({
    queryKey: ['catalog-merge-search', q],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/admin/catalog/parts`, {
        params: { q: q.trim(), status: 'verified' },
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.data.items;
    },
    enabled: q.trim().length > 0,
  });
  const targets = (data || []).filter((t) => t._id !== part._id);
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white">Fusionner « {part.reference} » dans…</h3>
        <p className="text-xs text-slate-400">Choisissez l'entrée validée à conserver. Les usages de « {part.reference} » y seront déplacés.</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entrée validée..."
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" />
        <div className="max-h-60 overflow-auto divide-y divide-slate-800">
          {targets.length === 0 ? (
            <p className="text-slate-500 text-sm py-3 text-center">Aucune cible validée trouvée.</p>
          ) : targets.map((t) => (
            <button key={t._id} onClick={() => onMerge(part._id, t._id)}
              className="block w-full text-left px-2 py-2 hover:bg-slate-800 text-sm text-slate-200">
              <span className="font-mono font-bold">{t.reference}</span> · {t.brand} · {t.canonicalName}
            </button>
          ))}
        </div>
        <div className="flex justify-end"><button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm">Annuler</button></div>
      </div>
    </div>
  );
}
